/**
 * ArchivedProvidersSupport — shared "Include archived providers" toggle
 * for provider dropdowns (Jun 2026).
 *
 * - Off by default; state is component-local so it never persists
 *   between sessions (resets whenever the host dialog closes/unmounts).
 * - Archived providers are fetched on demand from
 *   GET /api/providers?include_archived=true (tenant-scoped endpoint)
 *   and rendered greyed out with an "Archived" tag.
 */
import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useArchivedProviders() {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [archivedProviders, setArchivedProviders] = useState([]);
  const fetchedRef = useRef(false);

  const toggleArchived = useCallback(async (next) => {
    setIncludeArchived(next);
    if (next && !fetchedRef.current) {
      fetchedRef.current = true;
      try {
        const res = await axios.get(`${API}/providers`, {
          params: { include_archived: true },
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
        });
        const list = Array.isArray(res.data) ? res.data : [];
        setArchivedProviders(list.filter((p) => p.is_archived));
      } catch {
        fetchedRef.current = false;
      }
    }
  }, []);

  const resetArchived = useCallback(() => setIncludeArchived(false), []);

  // Merge archived providers (flagged is_archived) after the base list.
  const mergeProviders = useCallback((base) => {
    const list = Array.isArray(base) ? base : [];
    if (!includeArchived || archivedProviders.length === 0) return list;
    const seen = new Set(list.map((p) => p.id));
    return [...list, ...archivedProviders.filter((p) => !seen.has(p.id))];
  }, [includeArchived, archivedProviders]);

  return { includeArchived, toggleArchived, resetArchived, mergeProviders, archivedProviders };
}

// Row rendered at the top of a Radix SelectContent. pointerDown is
// prevented so clicking the toggle doesn't close the dropdown.
export function ArchivedProvidersToggleRow({ includeArchived, onToggle }) {
  return (
    <div
      data-testid="include-archived-providers-toggle"
      role="checkbox"
      aria-checked={includeArchived}
      aria-label="Include archived providers"
      className="flex items-center gap-2 px-2 py-1.5 mb-1 border-b border-gray-100 cursor-pointer select-none text-xs text-gray-600 hover:bg-gray-50 rounded-sm"
      onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(!includeArchived); }}
    >
      <input
        type="checkbox"
        checked={includeArchived}
        readOnly
        tabIndex={-1}
        className="h-3.5 w-3.5 pointer-events-none accent-teal-600"
      />
      <span>Include archived providers</span>
    </div>
  );
}

export function ArchivedProviderTag() {
  return (
    <span className="text-[10px] font-medium text-gray-500 bg-gray-100 border border-gray-300 rounded-full px-1.5 py-px">
      Archived
    </span>
  );
}
